import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { PageHeader } from '../../components/ui/PageHeader';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { apiClient } from '../../lib/api-client';
import { useAuth } from '../auth/auth-context';

interface Incident {
  id: string;
  title: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED';
  createdAt: string;
  automation: { id: string; name: string };
}

async function fetchIncidents(): Promise<{ data: Incident[]; meta: { total: number } }> {
  const res = await apiClient.get<{ data: Incident[]; meta: { total: number } }>('/incidents', {
    params: { page: 1, pageSize: 100 },
  });
  return res.data;
}

const SEV_COLOR: Record<Incident['severity'], string> = {
  LOW: 'bg-slate-100 text-slate-700',
  MEDIUM: 'bg-amber-100 text-amber-800',
  HIGH: 'bg-orange-100 text-orange-800',
  CRITICAL: 'bg-red-100 text-red-800',
};

export const SEV_LABEL: Record<Incident['severity'], string> = {
  LOW: 'Basse',
  MEDIUM: 'Moyenne',
  HIGH: 'Haute',
  CRITICAL: 'Critique',
};

export function IncidentsListPage() {
  const { user } = useAuth();
  const canDelete = user?.role === 'SUPER_ADMIN';
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['incidents', 'all'],
    queryFn: fetchIncidents,
    refetchInterval: 10_000,
  });

  const del = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/incidents/${id}`),
    onSuccess: () => {
      // Les notifs liées à l'incident sont cascade-deleted en base — on rafraîchit la
      // cloche immédiatement plutôt que d'attendre son refetchInterval (15 s).
      qc.invalidateQueries({ queryKey: ['incidents'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'bell'] });
    },
  });

  function handleDelete(id: string) {
    if (window.confirm('Supprimer définitivement cet incident ? Action irréversible.')) {
      del.mutate(id);
    }
  }

  return (
    <>
      <PageHeader
        helpTopic="incidents"
        title="Incidents"
        subtitle="Anomalies détectées sur le parc."
      />
      <section className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="overflow-x-auto surface">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Titre</th>
                <th className="hidden px-4 py-3 md:table-cell">Automatisation</th>
                <th className="px-4 py-3">Sévérité</th>
                <th className="hidden px-4 py-3 sm:table-cell">Statut</th>
                <th className="hidden px-4 py-3 lg:table-cell">Ouvert le</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(list.data?.data ?? []).map((i) => (
                <tr key={i.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-slate-900">{i.title}</td>
                  <td className="hidden px-4 py-2.5 text-slate-600 md:table-cell">
                    {i.automation.name}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${SEV_COLOR[i.severity]}`}
                    >
                      {SEV_LABEL[i.severity]}
                    </span>
                  </td>
                  <td className="hidden px-4 py-2.5 sm:table-cell">
                    <StatusBadge status={i.status} />
                  </td>
                  <td className="hidden px-4 py-2.5 text-slate-500 lg:table-cell">
                    {new Date(i.createdAt).toLocaleString('fr-FR')}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <Link
                        to={`/incidents/${i.id}`}
                        className="text-xs font-medium text-brand hover:underline"
                      >
                        Détail →
                      </Link>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDelete(i.id)}
                          disabled={del.isPending}
                          className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                        >
                          Supprimer
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {list.data && list.data.data.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    Aucun incident.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
